import React, { useEffect, useState, useMemo, useRef } from "react";
import { fetchDataFromApi } from "../../../../widgets/api";
import AppURL from "../../../../widgets/AppURL";
import { useReactToPrint } from "react-to-print";
import PrintIssueReturnSummaryReport from "./PrintIssueReturnSummaryReport";
import { pageTitle } from "../../../../store/softSlice";
import { useDispatch } from "react-redux";
import { PdfExportBtn } from "../../../../widgets/AppBtn";

const IssueReturnSummaryReportListView = ({setIconsButton, PageTitle}) => {
      const dispatch = useDispatch();

     useEffect(() => {
        dispatch(pageTitle({ title: PageTitle }));
        document.title = `${PageTitle}`;
        setIconsButton(<PdfExportBtn onClick={handlePrintList} />);
      }, []);
  const [listRpt, setListRpt] = useState([]);

  const fetchReport = async () => {
    try {
      const res = await fetchDataFromApi(
        AppURL.LibraryURL + "library-book-summary-report-list-view/"
      );
      setListRpt(res);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  /** GROUP → Faculty → Level */
  const groupedData = useMemo(() => {
    return listRpt.reduce((acc, row) => {
      const faculty = row.faculty || "Unknown Faculty";
      const level = row.level || "Unknown Level";

      acc[faculty] = acc[faculty] || {};
      acc[faculty][level] = acc[faculty][level] || [];

      acc[faculty][level].push(row);
      return acc;
    }, {});
  }, [listRpt]);

  /** Helper: calculate totals */
  const calculateTotals = (rows) =>
    rows.reduce(
      (t, r) => {
        t.total += r.total_books;
        t.issued += r.issued_books;
        t.returned += r.total_returned;
        t.available += r.total_books - r.issued_books;
        return t;
      },
      { total: 0, issued: 0, returned: 0, available: 0 }
    );

  const grandTotal = calculateTotals(listRpt);
  const handlePrintList = useReactToPrint({
    content: () => componentListRef.current,
  });
  const componentListRef = useRef();
  const RenderListComponent = React.forwardRef(() => (
    <PrintIssueReturnSummaryReport listRpt={listRpt} />
  ));
  return (
    <div className="space-y-6 overflow-x-auto font-public-sans">
      {Object.entries(groupedData).map(([faculty, levels]) => {
        const facultyRows = Object.values(levels).flat();
        const facultyTotal = calculateTotals(facultyRows);

        return (
          <div key={faculty} className="border rounded-md">
            {/* FACULTY HEADER */}
            <div className="bg-gray-200 px-4 py-2 font-bold text-lg">
              Faculty: {faculty}
            </div>

            {Object.entries(levels).map(([level, rows]) => {
              const levelTotal = calculateTotals(rows);

              return (
                <div key={level} className="p-3">
                  {/* LEVEL HEADER */}
                  <div className="font-semibold text-md mb-2">
                    Level: {level}
                  </div>

                  <table className="min-w-full border text-sm mb-3 font-public-sans">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border px-2 py-1 text-left">Program</th>
                        <th className="border px-2 py-1 text-center">
                          Year / Semester
                        </th>
                        <th className="border px-2 py-1 text-right">Total</th>
                        <th className="border px-2 py-1 text-right">Issued</th>
                        <th className="border px-2 py-1 text-right">Returned</th>
                        <th className="border px-2 py-1 text-right">
                          Available
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="border px-2 py-1">
                            {row.program}
                          </td>
                          <td className="border px-2 py-1 text-center">
                            {row.year_semester}
                          </td>
                          <td className="border px-2 py-1 text-right">
                            {row.total_books}
                          </td>
                          <td className="border px-2 py-1 text-right text-blue-600">
                            {row.issued_books}
                          </td>
                          <td className="border px-2 py-1 text-right text-green-600">
                            {row.total_returned}
                          </td>
                          <td className="border px-2 py-1 text-right font-semibold text-purple-600">
                            {row.total_books - row.issued_books}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    {/* LEVEL TOTAL */}
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold">
                        <td colSpan="2" className="border px-2 py-1 text-right">
                          Level Total
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {levelTotal.total}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {levelTotal.issued}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {levelTotal.returned}
                        </td>
                        <td className="border px-2 py-1 text-right">
                          {levelTotal.available}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })}

            {/* FACULTY TOTAL */}
            <div className="bg-gray-100 px-4 py-2 font-semibold">
              Faculty Total — Total: {facultyTotal.total} | Issued:{" "}
              {facultyTotal.issued} | Returned: {facultyTotal.returned} |
              Available: {facultyTotal.available}
            </div>
          </div>
        );
      })}

      {/* GRAND TOTAL */}
      <div className="bg-gray-300 px-4 py-3 font-bold text-lg">
        Grand Total — Total: {grandTotal.total} | Issued:{" "}
        {grandTotal.issued} | Returned: {grandTotal.returned} | Available:{" "}
        {grandTotal.available}
      </div>
      <div className='hidden'>
        <div ref={componentListRef}>
          <RenderListComponent />
        </div>
      </div>
    </div>
  );
};

export default IssueReturnSummaryReportListView;
